import { PlayerContextState } from '../../contexts/player';
import { PlayerStateFull } from '../../types/state';
import { Task, TaskContentAnswer } from '../../types/entities/task';
import { UpdatePlayerStateAsync } from './base';

export const mergeServerPlayerState = (
  prev: PlayerContextState,
  serverResponse: any
): PlayerContextState => {
  if (!serverResponse || !prev?.playerState) {
    return prev;
  }

  let payload = serverResponse;
  if (typeof payload === 'string') {
    try {
      payload = JSON.parse(payload);
    } catch (e) {
      console.error('Failed to parse server response in mergeServerPlayerState', e);
      return prev;
    }
  }

  // Unwrap potential envelope properties
  while (payload && typeof payload === 'object') {
    if (payload.data && typeof payload.data === 'object') {
      payload = payload.data;
    } else if (payload.result && typeof payload.result === 'object') {
      payload = payload.result;
    } else {
      break;
    }
  }

  // Extract server player state object
  const serverPlayerState: any =
    payload?.playerState ??
    payload?.PlayerState ??
    payload;

  if (!serverPlayerState || typeof serverPlayerState !== 'object') {
    return prev;
  }

  // Extract authoritative score
  const rawScore = serverPlayerState.score ?? serverPlayerState.Score;
  const serverScore =
    typeof rawScore === 'number'
      ? rawScore
      : typeof rawScore === 'string' && !isNaN(Number(rawScore))
        ? Number(rawScore)
        : undefined;

  // Extract authoritative tasks
  const rawTasks = serverPlayerState.tasks ?? serverPlayerState.Tasks;
  const serverTasks: any[] = Array.isArray(rawTasks) ? rawTasks : [];

  const currentLocalState = prev.playerState;
  const currentLocalTasks: Task[] = Array.isArray(currentLocalState.tasks)
    ? currentLocalState.tasks
    : [];

  // Deep merge tasks
  const mergedTasks: Task[] = currentLocalTasks.map((localTask) => {
    if (!localTask || !localTask.id) return localTask;

    const serverTask = serverTasks.find(
      (st: any) =>
        (st?.id ?? st?.Id)?.toString().toLowerCase() ===
        localTask.id?.toString().toLowerCase()
    );

    if (!serverTask) return localTask;

    // Merge status
    const taskStatus =
      serverTask.status ??
      serverTask.Status ??
      localTask.status;

    // Merge taskContentFormAnswers
    const localAnswers: TaskContentAnswer[] = Array.isArray(localTask.taskContentFormAnswers)
      ? localTask.taskContentFormAnswers
      : [];
    const rawServerAnswers = serverTask.taskContentFormAnswers ?? serverTask.TaskContentFormAnswers;
    const serverAnswers: any[] = Array.isArray(rawServerAnswers) ? rawServerAnswers : [];

    const mergedAnswers: TaskContentAnswer[] = localAnswers.map((localAns) => {
      if (!localAns || !localAns.formId) return localAns;

      const serverAns = serverAnswers.find(
        (sa: any) =>
          (sa?.formId ?? sa?.FormId)?.toString().toLowerCase() ===
          localAns.formId?.toString().toLowerCase()
      );

      if (!serverAns) return localAns;

      const rawAnsScore = serverAns.score ?? serverAns.Score;
      const ansScore =
        typeof rawAnsScore === 'number'
          ? rawAnsScore
          : typeof rawAnsScore === 'string' && !isNaN(Number(rawAnsScore))
            ? Number(rawAnsScore)
            : localAns.score;

      const ansIsCorrect =
        serverAns.isCorrectAnswer ??
        serverAns.IsCorrectAnswer ??
        localAns.isCorrectAnswer;

      const ansAttemptsLeft =
        serverAns.attemptsLeft ??
        serverAns.AttemptsLeft ??
        localAns.attemptsLeft;

      return {
        ...localAns,
        ...serverAns,
        score: ansScore,
        isCorrectAnswer: ansIsCorrect,
        attemptsLeft: ansAttemptsLeft
      };
    });

    // Add any server form answers not in local answers
    serverAnswers.forEach((serverAns: any) => {
      const serverFormId = (serverAns?.formId ?? serverAns?.FormId)?.toString().toLowerCase();
      if (
        serverFormId &&
        !mergedAnswers.some(
          (ma) => (ma?.formId)?.toString().toLowerCase() === serverFormId
        )
      ) {
        const rawAnsScore = serverAns.score ?? serverAns.Score;
        const ansScore =
          typeof rawAnsScore === 'number'
            ? rawAnsScore
            : typeof rawAnsScore === 'string' && !isNaN(Number(rawAnsScore))
              ? Number(rawAnsScore)
              : 0;

        mergedAnswers.push({
          ...serverAns,
          formId: serverAns.formId ?? serverAns.FormId,
          score: ansScore
        });
      }
    });

    // AR mini game score merge if present
    const serverAr = serverTask.dataARMiniGame ?? serverTask.DataARMiniGame;
    const mergedAr = serverAr
      ? { ...localTask.dataARMiniGame, ...serverAr }
      : localTask.dataARMiniGame;

    return {
      ...localTask,
      ...serverTask,
      status: taskStatus,
      taskContentFormAnswers: mergedAnswers,
      dataARMiniGame: mergedAr
    };
  });

  // Include any server tasks that do not exist locally
  serverTasks.forEach((serverTask: any) => {
    const serverTaskId = (serverTask?.id ?? serverTask?.Id)?.toString().toLowerCase();
    if (
      serverTaskId &&
      !mergedTasks.some(
        (mt) => (mt?.id)?.toString().toLowerCase() === serverTaskId
      )
    ) {
      mergedTasks.push(serverTask);
    }
  });

  const finalScore = serverScore !== undefined ? serverScore : currentLocalState.score;

  const updatedPlayerState: PlayerStateFull = {
    ...currentLocalState,
    ...serverPlayerState,
    score: finalScore,
    tasks: mergedTasks
  };

  return UpdatePlayerStateAsync(prev, updatedPlayerState);
};
